import { Box, Button, ButtonGroup, Tooltip as MUITooltip } from "@mui/material";
import { SxProps } from "@mui/system";
import React, { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatColorQuery } from "../../tools";
import ErrorBoundary from "../utils/error-boundary";
import ColorLink from "./ColorLink";

// constants for categories display management
const MAX_CATEGORIES_COUNT = 10;
const CATEGORY_MIN_PERCENTAGE = 1;

// constants for chart layout
const BAR_HEIGHT = 24;
const BAR_CATEGORY_GAP = 10;
const X_AXIS_HEIGHT = 40;

export type ColorSystem = "GIA" | "Langerman" | "Flat" | "Melee";

interface EquivalentColorsSet {
  system: string;
  color: string;
  equivalentColors: string[];
}

interface BackendColorSpreadItem {
  count: number;
  // A bar counts the stones of one color as recorded, and this names that color along with its
  // confirmed equivalents — named alongside, not merged in: they need not be equivalent to each
  // other, so there is no group the bar could stand for. It is all the backend sends: what the bar
  // reads and where a click goes are both written from it below. Null on the one bar counting the
  // stones with no value in this system at all: a scheme queried on a GIA color prices stones
  // carrying no Langerman grade, and those land in the Langerman spread's null bar, which is
  // dropped before display.
  equivalentColors: EquivalentColorsSet | null;
}

interface FrontendColorSpreadItem extends BackendColorSpreadItem {
  // What the bar reads, and the pricing query a click opens. Two different spellings of the
  // bar's one color — the label drops the system where the query keeps it, see formatSpreadColor.
  label: string;
  // Null on the synthetic "Other" bar, which is no single color to navigate to.
  color: string | null;
  percentage: number;
}

interface ColorSpread<T> {
  system: ColorSystem;
  items: T[];
}

interface ColorSpreadProps {
  hideIfSingleCategory: boolean;
  allowedColorSystems: ColorSystem[];
  colorSpreads: ColorSpread<BackendColorSpreadItem>[];
  style?: SxProps;
  // Hide the built-in "Color Spread" label / type selector — when the caller supplies its own header.
  hideHeader?: boolean;
}

// A spread is already grouped under its system's own header, so a label only needs what tells one
// bar from the next. Flat and Melee values are bare numbers that read as nothing on their own and
// keep their prefix; GIA and Langerman colors are already words. This is why the backend sends the
// color raw and leaves the spelling here — the pricing query the same bar links to always
// spells the system, so the two cannot share one string.
const formatSpreadColor = (system: string, rawValue: string) =>
  system === "Flat" || system === "Melee"
    ? formatColorQuery(system, rawValue)
    : `${rawValue}`;

// Keyed by system, but reading as the colors themselves: what a person calls the GIA system's
// values is "Fancy Color", not "GIA".
export const COLOR_SYSTEM_LABELS: Record<ColorSystem, string> = {
  GIA: "Fancy Color",
  Langerman: "Langerman Color",
  Flat: "Flat",
  Melee: "Melee",
};

/**
 * @param colorSpreads The list of color spreads returned from the backend (we know the items are sorted in descending order by count)
 * @returns The color spreads items are reworked for nice display in the chart
 */
export const prepareColorSpreads = (
  colorSpreads: ColorSpread<BackendColorSpreadItem>[]
): ColorSpread<FrontendColorSpreadItem>[] => {
  return colorSpreads
    .map((colorSpread) => {
      // the colorless bar counts the stones outside this system entirely, which is not a category
      const filtered = colorSpread.items.filter(
        ({ equivalentColors }) => !!equivalentColors
      );

      // compute proportions for each category (in %)
      const totalCount = filtered.reduce((acc, item) => acc + item.count, 0);
      const mapped: FrontendColorSpreadItem[] = filtered.map((item) => {
        // survived the filter above, so the color is there
        const { system, color } = item.equivalentColors!;
        return {
          ...item,
          // both written from the bar's color, which arrives as a raw per-system value
          label: formatSpreadColor(system, color),
          color: formatColorQuery(system, color),
          percentage: (100 * item.count) / totalCount,
        };
      });

      // now we want to limit the number of categories displayed
      let i = 0;
      while (
        i < mapped.length &&
        i < MAX_CATEGORIES_COUNT &&
        mapped[i].percentage >= CATEGORY_MIN_PERCENTAGE
      )
        i++;

      // in this case, all items can be retained as-is
      if (i === mapped.length) {
        return { system: colorSpread.system, items: mapped };
      }

      // if we are exactly at the limit, we need to keep one less item to make room for the "Other" category
      if (i === MAX_CATEGORIES_COUNT) {
        i--;
      }

      // the top i categories are kept as-is, the rest are merged into "Other"
      const unchangedCategories = mapped.slice(0, i);
      const mergedCategoriesMetrics = mapped.slice(i).reduce(
        (acc, item) => ({
          categoriesCount: acc.categoriesCount + 1,
          count: acc.count + item.count,
          percentage: acc.percentage + item.percentage,
        }),
        { categoriesCount: 0, count: 0, percentage: 0 }
      );
      const items = [
        ...unchangedCategories,
        {
          label: `Other (contains ${mergedCategoriesMetrics.categoriesCount} categories)`,
          color: null,
          count: mergedCategoriesMetrics.count,
          percentage: mergedCategoriesMetrics.percentage,
          equivalentColors: null,
        },
      ];

      return {
        system: colorSpread.system,
        items,
      };
    })
    .filter((colorSpread) => colorSpread.items.length > 0);
};

const BarChartCustomLabel = (props: any) => {
  const x =
    props.position === "right"
      ? props.x + props.width + props.offset
      : props.x + props.offset;
  const y = props.y + props.height / 2 + props.offset;
  const color = props.position === "right" ? "#808080" : "white";
  return (
    <text x={x} y={y} fill={color}>
      {props.value}
    </text>
  );
};

const BarChartCustomTooltip = (props: any) => {
  const { payload } = props;
  if (!payload) return null;
  if (!payload[0]) return null;
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        padding: 8,
        whiteSpace: "nowrap",
        border: "1px solid #ccc",
        backgroundColor: "white",
      }}
    >
      <ColorLink
        label={payload[0]?.payload.label}
        color={payload[0]?.payload.color}
      />
      <span>{(payload[0]?.value as number).toFixed(2)} %</span>
    </div>
  );
};

const ColorSpreadComponent = ({
  hideIfSingleCategory,
  allowedColorSystems,
  colorSpreads,
  style,
  hideHeader,
}: ColorSpreadProps) => {
  const preparedColorSpreads = useMemo(
    () => prepareColorSpreads(colorSpreads),
    [colorSpreads]
  );

  const availableColorSystems = preparedColorSpreads
    .map((spread) => spread.system)
    .filter((system) => allowedColorSystems.includes(system));

  const [displayedColorSystem, setDisplayedColorSystem] = useState<
    ColorSystem | undefined
  >(availableColorSystems[0]);

  const colorSpread = preparedColorSpreads.find(
    (spread) => spread.system === displayedColorSystem
  );

  if (!colorSpread) {
    return null;
  }

  if (hideIfSingleCategory && colorSpread.items.length <= 1) {
    return null;
  }

  const displayButtonGroup = allowedColorSystems.length > 1;

  return (
    <Box
      sx={{
        width: "100%",
        ...style,
      }}
    >
      {!hideHeader && (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "flex-end",
          position: "relative",
        }}
      >
        <span>Color Spread</span>

        {displayButtonGroup && (
          <ButtonGroup
            variant="outlined"
            aria-label="Color Spread Color System"
            style={{
              whiteSpace: "nowrap",
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
            }}
          >
            {allowedColorSystems.map((system) =>
              availableColorSystems.indexOf(system) === -1 ? (
                <MUITooltip
                  key={system}
                  title={`There is nothing to visualize for ${COLOR_SYSTEM_LABELS[system]}`}
                >
                  {/*
                  wrapping the Button in a span is necessary for the tooltip to work correctly on a disabled Button
                  https://mui.com/material-ui/react-tooltip/#disabled-elements
                */}
                  <span>
                    <Button disabled={true} variant="outlined">
                      {COLOR_SYSTEM_LABELS[system]}
                    </Button>
                  </span>
                </MUITooltip>
              ) : (
                <Button
                  key={system}
                  onClick={() => setDisplayedColorSystem(system)}
                  variant={
                    system === displayedColorSystem ? "contained" : "outlined"
                  }
                >
                  {COLOR_SYSTEM_LABELS[system]}
                </Button>
              )
            )}
          </ButtonGroup>
        )}
      </Box>
      )}

      {/*
      recharts takes some liberty with regards to overriding the barSize/barCategoryGap props passed to BarChart
      and it can be pretty annoying because we can have several ColorSpread charts on the same page and they would
      present different bar sizes and gaps between categories, hence I reverse engineered the layout of the charts
      so that we can calculate the exact height the charts needs so that our bar sizes and gaps are respected 
      */}
      <ResponsiveContainer
        width="100%"
        height={
          10 +
          BAR_HEIGHT * colorSpread.items.length +
          BAR_CATEGORY_GAP * colorSpread.items.length +
          X_AXIS_HEIGHT
        }
      >
        <BarChart
          data={colorSpread.items}
          layout="vertical"
          barSize={BAR_HEIGHT}
          barCategoryGap={BAR_CATEGORY_GAP / 2}
        >
          <XAxis
            type="number"
            unit="%"
            domain={[0, 100]}
            height={X_AXIS_HEIGHT}
          />
          <YAxis type="category" dataKey="label" hide />
          <Bar
            dataKey="percentage"
            fill="#118C67ff"
            activeBar={{
              fill: "#999999",
            }}
            onClick={({ color }) => {
              if (color === null) return;

              window.open(`/analytics/estimator?color=${color}`, "_blank");
            }}
            style={{ cursor: "pointer" }}
          >
            <LabelList
              dataKey="label"
              position={colorSpread.items.length > 1 ? "right" : "insideRight"}
              content={BarChartCustomLabel}
            />
          </Bar>
          <Tooltip cursor={false} content={BarChartCustomTooltip} />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

const ColorSpreadWithErrorBoundary = (props: ColorSpreadProps) => (
  <ErrorBoundary
    fallback={
      <div style={{ color: "red" }}>
        An error occurred while displaying the color spread.
      </div>
    }
  >
    <ColorSpreadComponent {...props} />
  </ErrorBoundary>
);

export default ColorSpreadWithErrorBoundary;
